import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';

import { useComponentSize } from '../hooks';
import Theme from '../common/NekoTheme';

const TooltipContainer = Styled.div`
	display: flex;
`;

const Tooltip = Styled.div`
	background-color: rgba(var(--neko-black-rgb), 0.8);
	border-radius: 4px;
	color: var(--neko-white);
	font-family: ${Theme.fontFamily};
	font-weight: normal;
	min-width: 180px;
	max-width: 280px;
	font-size: ${Theme.fontSizeText};
	padding: 8px 12px;
	position: absolute;
 	max-width: 280px;
	word-break: break-word;
	display: ${prop => prop.visible ? 'inline-block' : 'none'};
	z-index: 100;
	white-space: pre-wrap;

	&:before {
		content: "";
		position: absolute;
		border: 4px solid transparent;
	}

	&.top {
		&:before {
			margin-left: -4px;
			top: 100%;
			left: 50%;
			border-top: 4px solid rgba(var(--neko-black-rgb), 0.8);
		}
	}

	&.left {
		&:before {
			margin-top: -4px;
			top: 50%;
			right: -8px;
			border-left: 4px solid rgba(var(--neko-black-rgb), 0.8);
		}
	}

	&.right {
		&:before {
			margin-top: -4px;
			top: 50%;
			left: -8px;
			border-right: 4px solid rgba(var(--neko-black-rgb), 0.8);
		}
	}

	&.bottom {
		&:before {
			margin-left: -4px;
			top: -8px;
			left: 50%;
			border-bottom: 4px solid rgba(var(--neko-black-rgb), 0.8);
		}
	}

	${prop => prop.style}
`;

/**
 * @param {string} (variant) Tag Name
 */
const StyledNekoTooltip = props => {
	const { text = 'Hello world!', position = 'top' } = props;
	const [ show, setShow ] = useState(false);
	const { ref: tooltipRef, width: tooltipWidth, height: tooltipHeight } = useComponentSize();
	const { ref: childRef, width: childWidth, height: childHeight } = useComponentSize();

	const transformStyle = useMemo(() => {
		const pointerSize = 5;
		let transY = 0;
		let transX = 0;

		if (position === 'top') {
			transY = -1 * tooltipHeight - pointerSize;
			transX = -1 * (tooltipWidth / 2) + (childWidth / 2);
		}
		else if (position === 'bottom') {
			transY = childHeight + pointerSize;
			transX = -1 * (tooltipWidth / 2) + (childWidth / 2);
		}
		else if (position === 'left') {
			const diff = tooltipHeight - childHeight;
			transY = -1 * (diff / 2);
			transX = -1 * tooltipWidth - pointerSize;
		}
		else if (position === 'right') {
			const diff = tooltipHeight - childHeight;
			transY = -1 * (diff / 2);
			transX = childWidth + pointerSize;
		}
		
		return { 
			transform: `translateX(${transX}px) translateY(${transY}px)`
		};

	}, [position, tooltipWidth, tooltipHeight, childWidth, childHeight])

	return (
		<TooltipContainer className='neko-tooltip'>
			<Tooltip ref={tooltipRef} visible={show} className={position} style={transformStyle}>
				{text}
			</Tooltip>
			<div ref={childRef} onMouseEnter={() => text && setShow(true)} onMouseLeave={() => setShow(false)}>
				{props.children}
			</div>
		</TooltipContainer>
	);
};

const NekoTooltip = (props) => {
  return (<StyledNekoTooltip {...props} />);
}

NekoTooltip.propTypes = {
	text: PropTypes.string,
	position: PropTypes.oneOf(['top', 'right', 'bottom', 'left'])
};

NekoTooltip.defaultProps = {
	text: 'Hello world!',
	position: 'top'
};

export { NekoTooltip };
